import { showError } from '../ui/appToast';
import { reportUserVisibleFailure } from './sentryUser';

/** Mensaje default cuando el fallo es inesperado y no hay copy específico seguro. */
export const UNEXPECTED_FAILURE_USER_MESSAGE = 'Algo falló. Reintentá en un momento.';

const UNEXPECTED_FAILURE_HANDLED = Symbol.for('vuelto.unexpectedFailureHandled');

export type PresentUnexpectedFailureParams = {
  module: string;
  op: string;
  error: unknown;
  /** Título del toast (opcional). Si falta, `userMessage` va solo en text1. */
  title?: string;
  /** Cuerpo / mensaje único del toast. */
  userMessage?: string;
};

/**
 * Marca un error ya reportado (y opcionalmente toasteado) para que callers no dupliquen Issue/toast.
 * Usar al re-lanzar después de `presentUnexpectedFailure` / `reportUserVisibleFailure`.
 */
export function markUnexpectedFailureHandled<T extends Error>(error: T): T {
  Object.defineProperty(error, UNEXPECTED_FAILURE_HANDLED, {
    value: true,
    enumerable: false,
    configurable: true,
  });
  return error;
}

/** True si el catch superior no debe volver a reportar ni toastear el mismo fallo. */
export function isUnexpectedFailureHandled(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as Record<symbol, unknown>)[UNEXPECTED_FAILURE_HANDLED] === true
  );
}

/**
 * Toast + Sentry (`user_visible`) en un solo call.
 * Usar para fallos inesperados (5xx, bug, invoke roto). No para validación / permiso / negocio esperado.
 * No pasa `report: true` a `showError` — reporta una sola vez vía `reportUserVisibleFailure`.
 */
export function presentUnexpectedFailure(params: PresentUnexpectedFailureParams): void {
  const userMessage = params.userMessage ?? UNEXPECTED_FAILURE_USER_MESSAGE;
  try {
    reportUserVisibleFailure({
      module: params.module,
      op: params.op,
      error: params.error,
      title: params.title ?? userMessage,
      detail: params.title ? userMessage : undefined,
    });
  } catch {
    // Reporting must never replace the original failure path.
  }
  try {
    if (params.title) {
      showError(params.title, userMessage);
    } else {
      showError(userMessage);
    }
  } catch {
    // Toast must never throw into callers.
  }
}
